/**
 * Credential handoff from the URL.
 *
 * Two entry points receive a secret in the query string: the mobile app opens its WebView at
 * `/notesforMobile?token=<DRF key>` and password-reset mails link to
 * `/reset-password?uid=<uid>&token=…`. An inline script near the top of `public/index.html` (it MUST
 * stay above the first analytics loader) takes the token off the address bar before any tracker can
 * read the location, so GA/GTM, the Meta and Reddit pixels, Clarity, Segment, PostHog, Sentry
 * breadcrumbs, the referrer and browser history never see it:
 *
 * 1. the token is kept in memory (`window.__nudgeUrlHandoff`), by purpose: `mobile_auth`, or
 *    `password_reset[uid]` so two reset links opened in one tab never overwrite each other;
 * 2. best effort, it is also copied to sessionStorage (`url_handoff`, same JSON shape) so a reload
 *    still has it; storage may be blocked or full, and that must not stop step 3;
 * 3. the URL is always stripped; if that fails, `window.__nudgeSuppressAnalytics` is set and every
 *    tracker (the index.html snippets, and Clarity/Segment/PostHog here) stays off for the page load.
 *
 * This module is the React-side half: `stashSensitiveQueryParams` mirrors the inline script (the
 * tests run both against the same URLs), and the readers below look in memory first, then
 * sessionStorage, then the live URL (a stale cached index.html that predates the strip).
 */

/** sessionStorage key holding the handoff as JSON (`UrlHandoff`). */
export const URL_HANDOFF_KEY = "url_handoff";

/** Query parameters the inline script moves off the URL on every path. Keep in sync with index.html. */
export const SENSITIVE_QUERY_PARAMS: readonly string[] = ["token"];

export interface UrlHandoff {
  /** `/notesforMobile?token=` (and any path other than the reset page). */
  mobile_auth?: string;
  /** `/reset-password?uid=&token=`, keyed by the reset link's `uid`. */
  password_reset?: Record<string, string>;
}

type HandoffWindow = Window & { __nudgeUrlHandoff?: UrlHandoff; __nudgeSuppressAnalytics?: boolean };
const handoffWindow = (): HandoffWindow => window as HandoffWindow;

const isObject = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object";

const RESET_PASSWORD_PATH = /^\/reset-password\/?$/i;

/** Record `value` in `handoff` under the purpose the current path implies. Mirrors `put` in index.html. */
const putHandoff = (handoff: UrlHandoff, isReset: boolean, uid: string, value: string): void => {
  if (isReset) {
    if (!isObject(handoff.password_reset)) handoff.password_reset = {};
    handoff.password_reset[uid] = value;
  } else {
    handoff.mobile_auth = value;
  }
};

const memoryHandoff = (): UrlHandoff => {
  const w = handoffWindow();
  if (!isObject(w.__nudgeUrlHandoff)) w.__nudgeUrlHandoff = {};
  return w.__nudgeUrlHandoff as UrlHandoff;
};

const readStored = (): UrlHandoff => {
  try {
    const stored: unknown = JSON.parse(sessionStorage.getItem(URL_HANDOFF_KEY) || "{}");
    return isObject(stored) ? (stored as UrlHandoff) : {};
  } catch {
    return {};
  }
};

const writeStored = (handoff: UrlHandoff): void => {
  try {
    const resets = handoff.password_reset;
    if (isObject(resets) && Object.keys(resets).length === 0) delete handoff.password_reset;
    if (Object.keys(handoff).length === 0) {
      sessionStorage.removeItem(URL_HANDOFF_KEY);
    } else {
      sessionStorage.setItem(URL_HANDOFF_KEY, JSON.stringify(handoff));
    }
  } catch {
    // Storage blocked or full: the in-memory copy is authoritative for this page load.
  }
};

const ownString = (map: unknown, key: string): string | null => {
  if (!isObject(map) || !Object.prototype.hasOwnProperty.call(map, key)) return null;
  const value = map[key];
  return typeof value === "string" ? value : null;
};

/** True when index.html (or the mirror) could not strip a credential off the URL this page load. */
export const isAnalyticsSuppressed = (): boolean =>
  typeof window !== "undefined" && handoffWindow().__nudgeSuppressAnalytics === true;

/**
 * Move a sensitive query parameter from the URL into the handoff and strip it from the address bar.
 * Mirrors the inline script in `public/index.html`; a no-op when nothing sensitive is present.
 * Returns whether anything was moved.
 */
export const stashSensitiveQueryParams = (): boolean => {
  let stripped = false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (!params.has("token")) return false;
    const value = params.get("token") as string;
    params.delete("token");
    const path = window.location.pathname;
    const isReset = RESET_PASSWORD_PATH.test(path);
    const uid = params.get("uid") || "";
    putHandoff(memoryHandoff(), isReset, uid, value);
    try {
      const stored = readStored();
      putHandoff(stored, isReset, uid, value);
      sessionStorage.setItem(URL_HANDOFF_KEY, JSON.stringify(stored));
    } catch {
      // Best effort only; never let a storage failure keep the token on the URL.
    }
    try {
      const query = params.toString();
      window.history.replaceState(window.history.state, "", path + (query ? `?${query}` : "") + window.location.hash);
    } catch {
      // Checked below.
    }
    stripped = !new URLSearchParams(window.location.search).has("token");
  } catch {
    // Fall through: treat as not stripped.
  }
  if (!stripped) handoffWindow().__nudgeSuppressAnalytics = true;
  return true;
};

/**
 * Take the mobile app's bearer token (consumed: read exactly once). Memory first, then
 * sessionStorage, then the live URL (stale cached index.html; stripped as it is read).
 */
export const takeMobileAuthToken = (): string | null => {
  stashSensitiveQueryParams();
  const memory = memoryHandoff();
  const stored = readStored();
  const value = ownString(memory, "mobile_auth") ?? ownString(stored, "mobile_auth");
  delete memory.mobile_auth;
  if ("mobile_auth" in stored) {
    delete stored.mobile_auth;
    writeStored(stored);
  }
  return value;
};

/**
 * Read the reset token handed off for this `uid` (not consumed, so a reload of the reset page keeps
 * it; call `clearPasswordResetToken` once the reset succeeds). Only the entry matching `uid` is ever
 * returned, so another account's reset link opened in the same tab cannot be paired with this one.
 */
export const readPasswordResetToken = (uid: string): string | null => {
  stashSensitiveQueryParams();
  return ownString(memoryHandoff().password_reset, uid) ?? ownString(readStored().password_reset, uid);
};

/** Drop the reset token for this `uid` only; other accounts' pending resets are kept. */
export const clearPasswordResetToken = (uid: string): void => {
  const memory = memoryHandoff();
  if (isObject(memory.password_reset)) delete memory.password_reset[uid];
  const stored = readStored();
  if (isObject(stored.password_reset) && Object.prototype.hasOwnProperty.call(stored.password_reset, uid)) {
    delete stored.password_reset[uid];
    writeStored(stored);
  }
};
